/**
 * מה מצב הצ'קליסט, ואיך זה נראה.
 *
 * קודם כל צ'קליסט שלא התחיל הוצג באדום, כולל פתיחת סניף בחצות.
 * מסך שכולו אדום אינו אומר כלום, ומי שרגיל לראות אותו אדום
 * מפסיק להסתכל בו. עכשיו אדום שמור למה שהיה אמור לקרות ולא קרה.
 */
export type ChecklistState = 'done' | 'running' | 'waiting' | 'late' | 'unscheduled';

export type ChecklistStateInput = {
  completedAt: string | null;
  done: number;
  total: number;
  /** HH:MM:SS, או null כשאין מועד לתבנית. */
  dueTime: string | null;
  /** דקות מתחילת היום, בשעון המקומי של הרשת. */
  minutesNow: number;
};

/** דקות מתחילת היום מתוך HH:MM או HH:MM:SS. */
export function minutesOf(time: string): number {
  const [hours, minutes] = time.split(':');
  return Number(hours) * 60 + Number(minutes);
}

export function checklistState(input: ChecklistStateInput): ChecklistState {
  if (input.completedAt !== null) return 'done';
  if (input.done > 0) return 'running';

  // בלי מועד אי אפשר לדעת אם מאחרים, ולכן לא טוענים שכן
  if (input.dueTime === null) return 'unscheduled';

  return input.minutesNow >= minutesOf(input.dueTime) ? 'late' : 'waiting';
}

export const STATE_LABEL: Record<ChecklistState, string> = {
  done: 'הושלם',
  running: 'בתהליך',
  waiting: 'ממתין',
  late: 'לא התחיל',
  unscheduled: 'לא התחיל',
};

/**
 * `null` = בלי צבע סטטוס. "ממתין" אינו חדשות, וצביעה שלו היתה
 * מחזירה אותנו למסך שכולו צבוע.
 */
export const STATE_TONE: Record<ChecklistState, 'good' | 'warn' | 'bad' | null> = {
  done: 'good',
  running: 'warn',
  waiting: null,
  late: 'bad',
  unscheduled: null,
};

/** דקות מתחילת היום בשעון שנשלח, לשימוש כ minutesNow. */
export function minutesNowIn(now: Date): number {
  return now.getHours() * 60 + now.getMinutes();
}

/**
 * הבוררים שמוצעים במסך הניהול.
 *
 * 'late' ו 'unscheduled' חולקים תווית אחת ("לא התחיל") אבל הם
 * שני מצבים שונים: אחד עבר את המועד והשני בלי מועד בכלל. הבורר
 * מציג אחד, ולכן הוא מסנן לפי שניהם.
 */
export const STATE_FILTERS: { state: ChecklistState; label: string }[] = [
  { state: 'late', label: 'לא התחיל' },
  { state: 'running', label: 'בתהליך' },
  { state: 'done', label: 'הושלם' },
  { state: 'waiting', label: 'ממתין' },
];
